import { Injectable } from '@angular/core';

import {
  AnalysisGroupService,
  AnalysisGroupUsages,
} from 'app/providers/storage-api/analysis-group-service';
import {
  CalculationListItem,
  PredictiveService,
} from 'app/providers/storage-api/predictive.service';
import { Project } from 'app/providers/storage-api/project.service';
import { LoggerService } from 'app/services/logger.service';
import { Utils } from 'app/utils/utils';
import {
  CollectionService,
  CollectionUsages,
} from '../../../providers/storage-api/collection.service';
import { EnableClassTelemetry } from '../../../services/otel.service';
import { ActiveUse, ProjectItem } from '../services/project-detail.service';

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class ProjectItemActiveUseService {
  sortFn = (i1: ActiveUse, i2: ActiveUse) =>
    i1.activeUseType.localeCompare(i2.activeUseType) ||
    i1.displayName.localeCompare(i2.displayName);

  constructor(
    private collectionService: CollectionService,
    private analysisGroupService: AnalysisGroupService,
    private predictiveService: PredictiveService,
    private logger: LoggerService
  ) {}

  public async getCollectionUsages(item: ProjectItem, project: Project, collectionName: string) {
    const hasDriverAnalysis = item.collection.data.isDriverAnalysis;
    const promises = [];
    try {
      promises.push(this.collectionService.getCollectionUsages(project.id, collectionName));
      if (hasDriverAnalysis) {
        promises.push(
          this.predictiveService.listCalculations(project.id, item.id, {
            pageSize: 1024,
            sideloads: 'objectLabels',
          })
        );
      }
      const [collUsages, calcs] = await Promise.all(promises);

      if (item) {
        const { activeUses, hiddenDashCount } = this.getCollectionUses(collUsages);
        item.displayData.activeUse = activeUses;
        item.displayData.hiddenDashboardCount = hiddenDashCount;
        if (hasDriverAnalysis) {
          item.collection.driverAnalyses = calcs.items;
          const activeUsesKDA = this.getDashboardActiveUseKDA(calcs.items, project);
          item.displayData.activeUse = Utils.mergeArrays(
            item.displayData.activeUse,
            activeUsesKDA,
            (a, b) => a.activeUseType === b.activeUseType && a.id === b.id
          );
        }

        item.displayData.activeUse = item.displayData.activeUse.filter(
          (obj1, i, arr) => arr.findIndex((obj2) => obj2.id === obj1.id) === i
        );
        item.displayData.activeUse = item.displayData.activeUse.sort(this.sortFn);
        return item;
      }
    } catch (error) {
      this.logger.error('Error getting collection usages', { error });
    }
  }

  private getCollectionUses(collUsages: CollectionUsages) {
    let activeUses = [];
    const activeUseDashboards: ActiveUse[] = collUsages.dashboards.map((o) => ({
      id: o.id,
      displayName: o.name,
      activeUseType: 'dashboard',
    }));
    const activeUseGroups: ActiveUse[] = collUsages.analysisGroups.map((o) => ({
      id: o.id,
      displayName: o.name,
      activeUseType: 'group',
    }));
    activeUses = activeUses.concat(activeUseDashboards);
    activeUses = activeUses.concat(activeUseGroups);

    const hiddenDashCount = collUsages.hiddenDashboardCount;

    return { activeUses, hiddenDashCount };
  }

  public getDashboardActiveUseKDA(driverAnalyses: CalculationListItem[], project: Project) {
    let activeUsesKDA: ActiveUse[] = [];

    for (const kda of driverAnalyses) {
      for (const dashboard of project.dashboards) {
        const foundAnalysis = dashboard.widgets.find((widget) => widget.dataSourceId === kda.id);

        if (foundAnalysis) {
          activeUsesKDA.push({
            activeUseType: 'dashboard',
            displayName: dashboard.title,
            analysisID: foundAnalysis.id,
            ...dashboard,
          });
        }
      }
    }
    activeUsesKDA = activeUsesKDA.sort(this.sortFn);
    return activeUsesKDA;
  }

  public async getGroupUsages(item: ProjectItem, projectId: string, groupId: string) {
    try {
      const activeUses: AnalysisGroupUsages =
        await this.analysisGroupService.getAnalysisGroupUsages(projectId, groupId);
      item.displayData.hiddenDashboardCount = activeUses.hiddenDashboardCount;
      const activeUseDashboards: ActiveUse[] = activeUses.dashboards.map((o) => ({
        id: o.id,
        displayName: o.name,
        activeUseType: 'dashboard',
      }));
      item.displayData.activeUse = activeUseDashboards.sort(this.sortFn);
      return item;
    } catch (error) {
      this.logger.error('Error getting group usages', { error });
    }
  }

  public async getAnalysisUsages(item: ProjectItem, project: Project) {
    const collectionName = item.analysis.collectionName;
    const analysisName = item.analysis.name ?? item.analysis.analysisName;

    const activeUseDashboards: ActiveUse[] = [];
    try {
      const activeUses: CollectionUsages = await this.collectionService.getCollectionUsages(
        project.id,
        collectionName
      );

      for (const dash of activeUses.dashboards) {
        const dashboard = project.dashboards.find((d) => dash.id === d.id);

        const foundAnalysis = dashboard?.widgets.find(
          (widget) =>
            widget.dataSourceId === analysisName ||
            project.groups.some(
              (group) =>
                group.id === widget.dataSourceId &&
                group.analyses.some((a) => a['analysisName'] === analysisName)
            )
        );
        if (foundAnalysis) {
          activeUseDashboards.push({
            activeUseType: 'dashboard',
            displayName: dashboard.title,
            analysisID: foundAnalysis.id,
            ...dashboard,
          });
        }
        item.displayData.activeUse = activeUseDashboards.sort(this.sortFn);
        item.displayData.hiddenDashboardCount = activeUses.hiddenDashboardCount;
      }
      return item;
    } catch (error) {
      this.logger.error('Error getting analysis usages', { error });
    }
  }
}
